import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { notificationService } from '../services/notificationService';
import { Notification, NotificationListResponse } from '../types/notification';
import { useActivityDetection } from './useActivityDetection';
import { useAuth } from '../context/AuthContext';

export interface UseNotificationsOptions {
  /** Enable adaptive polling (default: true) */
  enablePolling?: boolean;
  /** Callback when new notifications arrive */
  onNewNotification?: (notification: Notification) => void;
  /** Fetch limit for notifications (default: 10) */
  limit?: number;
}

export interface UseNotificationsReturn {
  /** List of notifications */
  notifications: Notification[];
  /** Total count of notifications */
  total: number;
  /** Unread count */
  unreadCount: number;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch notifications */
  refetch: () => void;
  /** Mark notification as read */
  markAsRead: (notificationId: number) => Promise<void>;
  /** Mark all as read */
  markAllAsRead: () => Promise<void>;
  /** Current polling interval in ms */
  pollingInterval: number;
  /** Is user currently active */
  isUserActive: boolean;
}

/**
 * Hook for managing notifications with adaptive polling and toast notifications.
 *
 * Features:
 * - Adaptive polling based on user activity and unread count
 * - Toast notifications for new items
 * - Centralized notification fetching with React Query
 * - Activity detection (mouse, keyboard, window focus)
 *
 * Polling intervals:
 * - 15s when unread notifications exist and user is active
 * - 30s when user is active with no unread
 * - 60s when user is inactive
 * - Disabled when enablePolling is false
 */
export const useNotifications = (options: UseNotificationsOptions = {}): UseNotificationsReturn => {
  const {
    enablePolling = true,
    onNewNotification,
    limit = 10
  } = options;

  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isUserActive, setIsUserActive] = useState(true);
  const previousNotificationIds = useRef<Set<number>>(new Set());

  // Activity detection
  useActivityDetection(setIsUserActive, 60000); // 1 minute idle timeout

  // Only fetch notifications if user is authenticated
  const isAuthenticated = !!user;

  // Fetch unread count
  const { data: unreadCountData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: enablePolling && isAuthenticated ? 30000 : false, // Poll every 30s for count
    staleTime: 10000,
    enabled: isAuthenticated,
    retry: false
  });

  const unreadCount = unreadCountData?.unread_count || 0;

  // Calculate adaptive polling interval
  const pollingInterval = useMemo(() => {
    if (!enablePolling) return false;

    // User is inactive (tab not focused or no activity)
    if (!isUserActive) return 60000; // 1 minute

    // User is active with unread notifications
    if (unreadCount > 0) return 15000; // 15 seconds

    // User is active with no unread
    return 30000; // 30 seconds
  }, [enablePolling, isUserActive, unreadCount]);

  // Fetch notifications
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery<NotificationListResponse>({
    queryKey: ['notifications', 'list', limit],
    queryFn: () => notificationService.getNotifications({ limit }),
    refetchInterval: pollingInterval,
    staleTime: 10000,
    enabled: isAuthenticated,
    retry: false
  });

  // Memoize notifications to avoid triggering useEffect on every render
  const notifications = useMemo(() => data?.notifications || [], [data?.notifications]);
  const total = data?.total || 0;

  // Detect new notifications and trigger toast
  useEffect(() => {
    if (!notifications.length) return;

    const currentIds: Set<number> = new Set(notifications.map((n: Notification) => n.notification_id));

    // Find new notifications (not in previous set)
    const newNotifications = notifications.filter(
      (n: Notification) => !previousNotificationIds.current.has(n.notification_id) && !n.is_read
    );

    // Update previous IDs
    previousNotificationIds.current = currentIds;

    // Trigger callback for new notifications
    if (newNotifications.length > 0 && onNewNotification) {
      // Only show the most recent new notification to avoid spam
      onNewNotification(newNotifications[0]);
    }
  }, [notifications, onNewNotification]);

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: number) => notificationService.markAsRead(notificationId),
    onSuccess: () => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });

  const markAsRead = useCallback(
    async (notificationId: number) => {
      await markAsReadMutation.mutateAsync(notificationId);
    },
    [markAsReadMutation]
  );

  const markAllAsRead = useCallback(
    async () => {
      await markAllAsReadMutation.mutateAsync();
    },
    [markAllAsReadMutation]
  );

  return {
    notifications,
    total,
    unreadCount,
    isLoading,
    error: error as Error | null,
    refetch,
    markAsRead,
    markAllAsRead,
    pollingInterval: typeof pollingInterval === 'number' ? pollingInterval : 0,
    isUserActive
  };
};
